import {merge, Observable, of, OperatorFunction, pipe} from 'rxjs';
import {filter, mergeMap} from 'rxjs/operators';
import {SecurityService} from '../../../components/security';
import {WebSocketMessage} from '../../../components/streaming/streaming-interfaces';
import {SubscriptionSubject} from '../../../components/streaming/subscription-streaming.service';
import {SubscriptionSubjectType} from '../../../components/streaming/subscription-subject-type';
import {ArrayEncodedMdEntry, MdEntryFieldIndex} from '../../../models/common-ui-market-data/array-encoded-md-entry';
import {
    ArrayEncodedMdMessage,
    ArrayEncodedMdSnapshotMessage,
    ArrayEncodedMdUpdateMessage,
    MdMessageFieldIndex
} from '../../../models/common-ui-market-data/array-encoded-md-message';
import {MarketDataEntryType} from '../../../models/common-ui-market-data/market-data.enums';
import {Security} from '../../../models/security';
import {formatSecurityKey, SecurityKey} from '../../../models/security-key';
import {createEmptyQuote} from './empty-quote';
import {Quote} from './quote';

enum QuotesWebSocketMessageType {
    QUOTES_SNAPSHOT = 'QUOTES_SNAPSHOT',
    QUOTES_UPDATE   = 'QUOTES_UPDATE'
}

interface QuotesWebSocketMessage extends WebSocketMessage {
    messageType: QuotesWebSocketMessageType;
    data: ArrayEncodedMdMessage[];
}

interface QuotesSnapshotWebSocketMessage extends QuotesWebSocketMessage {
    messageType: QuotesWebSocketMessageType.QUOTES_SNAPSHOT;
    data: ArrayEncodedMdSnapshotMessage[];
}

interface QuotesUpdateWebSocketMessage extends QuotesWebSocketMessage {
    messageType: QuotesWebSocketMessageType.QUOTES_UPDATE;
    data: ArrayEncodedMdUpdateMessage[];
}

export class QuotesSubscriptionSubject {

    public type: SubscriptionSubjectType<Quote>;

    protected aggregationSuffix: string;

    protected aggregatedMessageTypeToCommonDict: {[aggregatedMessageType: string]: QuotesWebSocketMessageType};

    private dataBank: {
        [securityKeyStr: string]: Quote
    } = {};

    constructor(
        protected aggregationScale: number,
        protected securityService: SecurityService
    ) {
        this.updateAggregationSuffix();
        this.createWebSocketMessageTypes();
        this.createSubjectType();
    }

    protected createSubjectType(): void {
        this.type = {
            name: `QUOTES${this.aggregationSuffix}`,
            isMultiSubject: true,
            operations: {
                SUBSCRIBE: `SUBSCRIBE_QUOTES${this.aggregationSuffix}`,
                UNSUBSCRIBE: `UNSUBSCRIBE_QUOTES${this.aggregationSuffix}`
            },
            messagePipe: pipe(
                filter((message: WebSocketMessage) =>
                    !!this.aggregatedMessageTypeToCommonDict[message.messageType]),

                mergeMap((message: WebSocketMessage): Observable<Quote> => {
                    const quotes: Quote[] = this.readQuotesMessage(message);

                    return of(...quotes);
                })
            ),
            subjectPipeFactory: subject => this.createSubjectPipe(subject),
            deprecateSubject: subject => this.deprecateSubject(subject)
        };
    }

    protected updateAggregationSuffix(): void {
        this.aggregationSuffix = `#${this.aggregationScale || 1}`;
    }

    protected createWebSocketMessageTypes(): void {
        const snapshotMessage = [QuotesWebSocketMessageType.QUOTES_SNAPSHOT, this.aggregationSuffix].join('');
        const updateMessage = [QuotesWebSocketMessageType.QUOTES_UPDATE, this.aggregationSuffix].join('');

        this.aggregatedMessageTypeToCommonDict = {
            [snapshotMessage]: QuotesWebSocketMessageType.QUOTES_SNAPSHOT,
            [updateMessage]:   QuotesWebSocketMessageType.QUOTES_UPDATE
        };

        // Support old format of answers without #1
        if (this.aggregationScale === 1 && this.aggregationSuffix) {
            this.aggregatedMessageTypeToCommonDict[QuotesWebSocketMessageType.QUOTES_SNAPSHOT] =
                QuotesWebSocketMessageType.QUOTES_SNAPSHOT;
            this.aggregatedMessageTypeToCommonDict[QuotesWebSocketMessageType.QUOTES_UPDATE] =
                QuotesWebSocketMessageType.QUOTES_UPDATE;
        }
    }

    protected resolveReceivedSecurity<T extends SecurityKey | Security>(security: T): T {
        return security;
    }

    protected readQuotesMessage(message: QuotesWebSocketMessage): Quote[] {
        let quotes: Quote[];

        // Support forced #1 aggregation
        const msgType = this.aggregatedMessageTypeToCommonDict[message.messageType];

        switch (msgType) {

            case QuotesWebSocketMessageType.QUOTES_SNAPSHOT:
                quotes = this.readQuotesSnapshot(message.data as ArrayEncodedMdSnapshotMessage[]);
                break;

            case QuotesWebSocketMessageType.QUOTES_UPDATE:
                quotes = this.readQuotesUpdate(message.data as ArrayEncodedMdUpdateMessage[]);
                break;

            default:
                quotes = [];
                break;
        }

        return quotes;
    }

    protected readQuotesSnapshot(mdMessages: ArrayEncodedMdSnapshotMessage[]): Quote[] {
        return mdMessages.map((mdMessage: ArrayEncodedMdSnapshotMessage) => {
            const [mdReqId, securityIncrementId, mdEntries, rawSecurity] = mdMessage;
            const security = this.resolveReceivedSecurity(rawSecurity);
            const securityKeyStr = formatSecurityKey(security);

            const quote: Quote = {
                mdReqId,
                volume: 0,
                securityIncrementId,

                security,
                securityKeyStr,

                askTime: null,
                bidTime: null,
                tradeTime: null,
                ask: null,
                askVolume: null,
                bid: null,
                bidVolume: null,
                trade: null,
                tradeVolume: null,
                fundingRate: null,

                high: null,
                low: null,
                open: null,
                prevClose: null
            };

            this.readMdEntries(mdEntries, quote);

            this.dataBank[securityKeyStr] = quote;

            this.securityService.cacheSecurity(security);

            return quote;
        });
    }

    protected readQuotesUpdate(mdMessages: ArrayEncodedMdUpdateMessage[]): Quote[] {
        const quotes: Quote[] = [];

        mdMessages.forEach((mdMessage: ArrayEncodedMdUpdateMessage) => {
            const securityKey = this.resolveReceivedSecurity(mdMessage[MdMessageFieldIndex.SECURITY_KEY]);
            const knowQuote: Quote = this.getKnownQuote(securityKey);

            if (knowQuote) {
                this.readMdEntries(mdMessage[MdMessageFieldIndex.ENTRIES], knowQuote);

                quotes.push(knowQuote);
            }
        });

        return quotes;
    }

    protected readMdEntries(mdEntries: ArrayEncodedMdEntry[], quote: Quote): void {
        mdEntries.forEach(mdEntry => {
            switch (mdEntry[MdEntryFieldIndex.TYPE]) {
                case MarketDataEntryType.SESSION_VOLUME:
                    quote.volume = mdEntry[MdEntryFieldIndex.PX];
                    break;
                case MarketDataEntryType.SESSION_HIGH_PRICE:
                    quote.high = mdEntry[MdEntryFieldIndex.PX];
                    break;
                case MarketDataEntryType.SESSION_LOW_PRICE:
                    quote.low = mdEntry[MdEntryFieldIndex.PX];
                    break;
                case MarketDataEntryType.OPENING_PRICE:
                    quote.open = mdEntry[MdEntryFieldIndex.PX];
                    break;
                case MarketDataEntryType.CLOSING_PRICE:
                    quote.prevClose = mdEntry[MdEntryFieldIndex.PX];
                    break;
                case MarketDataEntryType.TRADE:
                    quote.trade = mdEntry[MdEntryFieldIndex.PX];
                    quote.tradeVolume = mdEntry[MdEntryFieldIndex.SIZE];
                    quote.tradeTime = mdEntry[MdEntryFieldIndex.TIME];
                    break;
                case MarketDataEntryType.OFFER_ORDER:
                    quote.ask = mdEntry[MdEntryFieldIndex.PX];
                    quote.askVolume = mdEntry[MdEntryFieldIndex.SIZE];
                    quote.askTime = mdEntry[MdEntryFieldIndex.TIME];
                    break;
                case MarketDataEntryType.BID_ORDER:
                    quote.bid = mdEntry[MdEntryFieldIndex.PX];
                    quote.bidVolume = mdEntry[MdEntryFieldIndex.SIZE];
                    quote.bidTime = mdEntry[MdEntryFieldIndex.TIME];
                    break;
                case MarketDataEntryType.FUNDING_RATE:

                    /**
                     * Funding rate has a fixed decimal count, different from price or volume,
                     * that's why it passed not in price or volume field of MdEntry.
                     * It's requirement on API side.
                     */
                    quote.fundingRate = JSON.parse(mdEntry[MdEntryFieldIndex.ORIGINATOR]);
                    break;
            }
        });
    }

    protected getKnownQuote(securityKey: SecurityKey): Quote {
        const securityKeyStr = formatSecurityKey(securityKey);

        return this.dataBank[securityKeyStr];
    }

    protected createSubjectPipe(subject: SubscriptionSubject): OperatorFunction<Quote, Quote> {
        const knownQuotes = subject
            .map(securityKeyStr => {
                const knownQuote = this.dataBank[securityKeyStr];

                return knownQuote || createEmptyQuote(securityKeyStr, this.securityService);
            });

        return pipe(
            filter((quote: Quote) => subject.includes(quote.securityKeyStr)),
            source => merge(of(...knownQuotes), source)
        );
    }

    protected deprecateSubject(subject: SubscriptionSubject): void {
        subject.forEach(securityKeyStr => delete this.dataBank[securityKeyStr]);
    }

}
